import { useHistory } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import {
  Button,
  SearchBar,
  SearchIcon,
  Loading,
  InfiniteScroll,
} from '@trufla-technology/trumarket-react-design-system';
import { SortingLine } from 'app/components/SortingLine/index';
import { useGetTeams } from '@trufla-technology/trufla-trudomain/dist/lib/truMarket';
import { debounce } from 'lodash';
import { TeamCardContent } from '../../Teams/TeamCardContent';
import { useAuthContext } from '@trufla-technology/trufla-trudomain/dist/lib';
import { GetTeamsResponse } from '@trufla-technology/trufla-trudomain/dist/lib/truMarket/types';

type defaultSortType = {
  sortBy: 'name' | 'usersCount' | 'createdAt' | 'updatedAt';
  sortOrder: 'asc' | 'desc';
};
const defaultSort: defaultSortType = {
  sortBy: 'name',
  sortOrder: 'asc',
};

export const Teams = () => {
  const { isPermitted } = useAuthContext();
  const [teamsList, setTeamsList] = useState<
    NonNullable<GetTeamsResponse['data']>
  >([]);

  const {
    data,
    paging,
    loading,
    handleSort,
    handleFilter,
    sortIndex,
    handleOffset,
    query,
  } = useGetTeams({
    paginate: {
      limit: 30,
      offset: 0,
    },
    sort: [defaultSort],
  });
  const history = useHistory();
  const handleRoute = () => {
    history.push('/configurations/teams/create');
  };
  const debouncedSearch = useRef(
    debounce(async (searchValue) => {
      setTeamsList([]);
      handleFilter({
        name: searchValue.length > 0 ? searchValue : undefined,
      });
    }, 600),
  ).current;
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    if (!data?.length) {
      return;
    }
    if (
      teamsList.length! < query.paginate?.offset! + paging?.limit! &&
      teamsList.length! < paging?.count!
    ) {
      setTeamsList([...teamsList, ...data]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const loadMore = () => {
    handleOffset(query.paginate?.limit! + query.paginate?.offset!);
  };

  return (
    <div className="all-teams">
      <div className="header">
        <p>Teams {paging?.count ? `(${paging?.count})` : ''}</p>
        {isPermitted('createTeam') && (
          <Button onClick={handleRoute} type="primary">
            Add Team
          </Button>
        )}
      </div>
      <div className="search-sorting-bar">
        <SearchBar
          placeholder="Filter"
          onSearch={(e: string) => {
            debouncedSearch(e);
          }}
          isSecondary
          icon={<SearchIcon fill="var(--tru-black)" />}
        />
        <div className="sorting-line-wrapper">
          <SortingLine
            items={[
              { key: 'name', label: 'Name' },
              { key: 'usersCount', label: 'Teams' },
              { key: 'createdAt', label: 'Created' },
              { key: 'updatedAt', label: 'Modified' },
            ]}
            onChange={(e: any) => {
              setTeamsList([]);
              handleSort(e);
            }}
            sortingIndex={{
              key: Object.keys(sortIndex)[0],
              label: Object.values(sortIndex)[0] as string,
            }}
          />
        </div>
      </div>
      <div className="team-list-scroll">
        {loading && teamsList.length === 0 ? (
          <div className="loading-teams">
            <Loading />
          </div>
        ) : (
          <div className="infinite-team-list">
            <InfiniteScroll
              data={teamsList}
              hasMore={!!!(teamsList.length! >= paging?.count!)}
              height="66vh"
              itemContent={(_index, team) => {
                return (
                  <TeamCardContent key={team?.key} team={team} lastUpdated />
                );
              }}
              loadMore={loadMore}
              emptyPlaceHolder={
                <div className="empty-teams">No Teams Found</div>
              }
            />
            {loading && teamsList.length && (
              <div className="loading-wrapper">
                <Loading />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
